import React from 'react';
import { ProjectLibraryView } from '../components/projects';
import { renameProject, deleteProject } from '../apiService';
import { useProjectStore } from '../stores/projectStore';
import { useAuth } from '../context/AuthContext';
import { useSessionActions } from '../context/SessionContext';
import { useShellUiActions } from '../context/ShellUiContext';
import { useActiveView } from '../router/useUrlNavigation';
import { useToast } from '../components/ui/Toast';

// BAR-40 S4: /projects route. Replaces the mainView==='projects' ternary branch
// verbatim. The rename/delete closures keep their exact behavior — including the
// BAR-53 "deleted the open project" teardown (clear active project + ephemeral
// state + local panels) before refreshing the list.
export default function ProjectsRoute() {
  const { showToast } = useToast();
  const { userData } = useAuth();
  // The logged-in owner id for list/rename/delete. Empty only in the brief
  // window before userData hydrates; the mutation handlers below refuse to
  // act on an empty owner (BAR-40 review).
  const userId = userData?.email || '';
  const { resetEphemeralProjectState } = useSessionActions();
  const { handleSelectProject, resetMainAppLocalPanels, navigateToView, beginBlankDraft } = useShellUiActions();
  const { projectsFilter } = useActiveView();
  const loadProjectList = useProjectStore((s) => s.loadProjectList);
  const clearActiveProject = useProjectStore((s) => s.clearActiveProject);
  const shelveProject = useProjectStore((s) => s.shelveProject);
  const unshelveProject = useProjectStore((s) => s.unshelveProject);

  return (
    <div className="h-full overflow-y-auto">
      <ProjectLibraryView
        userId={userId}
        initialTypeFilter={projectsFilter}
        onProjectSelect={handleSelectProject}
        onNewProject={(type) => {
          // Empty-state CTAs drop straight into an empty research / build
          // workspace (a topic-less blank draft) — same as the left-nav
          // Regulatory Research / Build & Artifacts entries. The project is
          // created lazily on the user's first chat message.
          beginBlankDraft(type);
        }}
        onRenameProject={async (projectId: string, newName: string) => {
          if (!userId) {
            showToast({
              variant: 'warning',
              message: 'Still loading your account — please try again in a moment.',
            });
            return;
          }
          const result = await renameProject(projectId, userId, newName);
          if (result.success) {
            loadProjectList(userId);
          } else {
            showToast({
              variant: 'error',
              message: `Failed to rename project: ${result.error}`,
            });
          }
        }}
        onDeleteProject={async (projectId: string) => {
          if (!userId) return { success: false, error: 'Still loading your account — please try again.' };
          const result = await deleteProject(projectId, userId);
          if (result.success) {
            // BAR-53: if we just deleted the currently-open
            // project, clear its active slice + ephemeral UI.
            // The raw deleteProject API call (unlike the store's
            // archiveProject) does not touch the store, so the
            // deleted project's workspace/chat would otherwise
            // linger.
            if (useProjectStore.getState().activeProject?.project_id === projectId) {
              await clearActiveProject({ discardPendingChanges: true });
              // Full ephemeral teardown (App's helper, passed as a prop) —
              // sibling Zustand stores + hasReconnectedSession + all
              // ephemerals, not just the streaming subset.
              if (typeof resetEphemeralProjectState === 'function') resetEphemeralProjectState();
              resetMainAppLocalPanels();
              navigateToView('projects');
            }
            // Refresh the project list
            loadProjectList(userId);
          }
          // Return the result so the library can
          // aggregate per-item outcomes for batch
          // deletes; the caller surfaces errors.
          return result;
        }}
        onArchiveProject={async (projectId: string) => {
          if (!userId) return { success: false, error: 'Still loading your account — please try again.' };
          // shelveProject sets status=archived_visible and (if it's the open
          // project) clears the active slice. Mirror the delete teardown so the
          // workspace/chat of the just-archived open project doesn't linger.
          const wasActive = useProjectStore.getState().activeProject?.project_id === projectId;
          const ok = await shelveProject(projectId, userId);
          if (ok && wasActive) {
            if (typeof resetEphemeralProjectState === 'function') resetEphemeralProjectState();
            resetMainAppLocalPanels();
            navigateToView('projects');
          }
          return ok ? { success: true } : { success: false, error: 'Failed to archive project' };
        }}
        onRestoreProject={async (projectId: string) => {
          if (!userId) return { success: false, error: 'Still loading your account — please try again.' };
          const ok = await unshelveProject(projectId, userId);
          return ok ? { success: true } : { success: false, error: 'Failed to restore project' };
        }}
      />
    </div>
  );
}
